// Operator options for the FilterBuilderPanel, keyed by MUI X column type.
// Each entry maps to the operators supported by the backend QueryBuilder.

export interface OperatorOption {
  value: string;
  label: string;
  /** True if this operator does not need a value input */
  valueless?: boolean;
}

export const OPERATORS_BY_TYPE: Record<string, OperatorOption[]> = {
  string: [
    { value: "contains", label: "contains" },
    { value: "doesNotContain", label: "does not contain" },
    { value: "equals", label: "equals" },
    { value: "!=", label: "not equals" },
    { value: "startsWith", label: "starts with" },
    { value: "endsWith", label: "ends with" },
    { value: "isAnyOf", label: "is any of" },
    { value: "isEmpty", label: "is empty", valueless: true },
    { value: "isNotEmpty", label: "is not empty", valueless: true },
    { value: "isNull", label: "is null", valueless: true },
    { value: "isNotNull", label: "is not null", valueless: true },
  ],
  number: [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: ">", label: ">" },
    { value: ">=", label: ">=" },
    { value: "<", label: "<" },
    { value: "<=", label: "<=" },
    { value: "isAnyOf", label: "is any of" },
    { value: "isNull", label: "is null", valueless: true },
    { value: "isNotNull", label: "is not null", valueless: true },
  ],
  date: [
    { value: "is", label: "is" },
    { value: "not", label: "is not" },
    { value: "after", label: "after" },
    { value: "onOrAfter", label: "on or after" },
    { value: "before", label: "before" },
    { value: "onOrBefore", label: "on or before" },
    { value: "isNull", label: "is null", valueless: true },
    { value: "isNotNull", label: "is not null", valueless: true },
  ],
  dateTime: [
    { value: "is", label: "is" },
    { value: "not", label: "is not" },
    { value: "after", label: "is after" },
    { value: "onOrAfter", label: "is on or after" },
    { value: "before", label: "is before" },
    { value: "onOrBefore", label: "is on or before" },
    { value: "isNull", label: "is null", valueless: true },
    { value: "isNotNull", label: "is not null", valueless: true },
  ],
  boolean: [
    { value: "=", label: "is" },
    { value: "isNull", label: "is null", valueless: true },
    { value: "isNotNull", label: "is not null", valueless: true },
  ],
  singleSelect: [
    { value: "equals", label: "equals" },
    { value: "doesNotEqual", label: "not equals" },
    { value: "isAnyOf", label: "is any of" },
    { value: "isEmpty", label: "is empty", valueless: true },
    { value: "isNotEmpty", label: "is not empty", valueless: true },
    { value: "isNull", label: "is null", valueless: true },
    { value: "isNotNull", label: "is not null", valueless: true },
  ],
  multiSelect: [
    { value: "containsAny", label: "contains any of" },
    { value: "containsAll", label: "contains all of" },
    { value: "isAnyOf", label: "is any of" },
    { value: "isEmpty", label: "is empty", valueless: true },
    { value: "isNotEmpty", label: "is not empty", valueless: true },
    { value: "isNull", label: "is null", valueless: true },
    { value: "isNotNull", label: "is not null", valueless: true },
  ],
};

/** Operators that require an array value input (multi-chip UI) */
export const ARRAY_VALUE_OPERATORS = new Set([
  "isAnyOf",
  "containsAny",
  "containsAll",
]);

/** Operators that require no value input */
export const VALUELESS_OPERATORS = new Set([
  "isEmpty",
  "isNotEmpty",
  "isNull",
  "isNotNull",
]);

/** The single operator of a rolled-up supplier column (see SupplierRollupCell). */
export const ROLLUP_OPERATORS: OperatorOption[] = [
  { value: "rollup", label: "matches" },
];

/** Get operators for a given column type; falls back to string operators */
export function getOperatorsForType(type?: string): OperatorOption[] {
  return OPERATORS_BY_TYPE[type ?? "string"] ?? OPERATORS_BY_TYPE.string;
}
